import { useMemo } from "react";

import CheckboxV2 from "components/CheckboxV2";
import { ChevronDownIcon, ChevronUpIcon } from "components/icons";

import { useTranslationContext } from "context/TranslationContext";
import cn from "lib/cn";

import { TPagination, TSort, TTabeField } from "../types";

interface TableHeaderProps {
  fields: TTabeField[];
  hasCheckbox?: boolean;
  isSelectedAllPage?: boolean;
  loading?: boolean;
  selectedRows?: string[];
  pagination?: TPagination;
  totalRecord?: number;
  onSelectAllCurrentPage?: (checked: boolean) => void;
  onSelectAllPage?: (isSelectAll: boolean) => void;
  onSort?: (sort: TSort) => void;
}

const TableHeader = ({
  fields,
  hasCheckbox,
  isSelectedAllPage,
  loading,
  pagination,
  selectedRows,
  totalRecord = 0,
  onSelectAllCurrentPage,
  onSelectAllPage,
  onSort,
}: TableHeaderProps) => {
  const { t } = useTranslationContext();
  const showColumn = useMemo(() => {
    return !isSelectedAllPage && !selectedRows?.length;
  }, [isSelectedAllPage, selectedRows]);

  const handleOnSort = (prevSort?: TSort) => {
    if (!prevSort || !onSort) return;

    let sort: "asc" | "desc" | "none" = "none";
    switch (prevSort.type) {
      case "none":
        sort = "asc";
        break;
      case "asc":
        sort = "desc";
        break;
      default:
        sort = "none";
        break;
    }

    onSort({
      fieldName: prevSort.fieldName,
      type: sort,
      key: prevSort.key,
    });
  };

  const handleSelectAllCurrentPage = (checked: boolean) => {
    if (!loading && onSelectAllCurrentPage) {
      let isChecked = checked;
      if (isSelectedAllPage || selectedRows?.length === totalRecord) {
        isChecked = false;
      }
      onSelectAllCurrentPage(isChecked);
    }
  };

  const handleSelectAllPage = () => {
    if (!loading && onSelectAllPage) {
      onSelectAllPage(!isSelectedAllPage);
    }
  };

  return (
    <div className="w-full text-base bg-primary-50 text-gray-900 ">
      <div className="min-h-[1.875rem] px-[1.58rem]">
        <div
          className={cn(
            "flex items-center w-full gap-2 min-h-[1.875rem]",
            showColumn ? "justify-between" : "justify-start"
          )}
        >
          {hasCheckbox && (
            <div className="mt-[0.3rem]" style={{ width: "25px" }}>
              <CheckboxV2
                checked={isSelectedAllPage}
                isIndeterminate={
                  !!selectedRows?.length &&
                  selectedRows.length !== pagination?.totalItem
                }
                onCheck={handleSelectAllCurrentPage}
              />
            </div>
          )}

          {!showColumn ? (
            <div className="text-night text-base">
              {isSelectedAllPage
                ? pagination?.totalItem
                : selectedRows?.length || 0}{" "}
              {t("total_selected_rows_label")} |{" "}
              <span
                className="text-primary-500 cursor-pointer"
                onClick={handleSelectAllPage}
              >
                {isSelectedAllPage
                  ? t("clear_selection")
                  : `${t("select_all")} ${pagination?.totalItem || 0} ${t("rows")}`}
              </span>
            </div>
          ) : (
            fields.map((field) => (
              <div
                key={field.key}
                className={cn(
                  "font-normal text-gray-900",
                  field.sort ? "cursor-pointer" : undefined,
                  field?.headerClassName
                )}
                style={{
                  width: field.width ? field.width : undefined,
                }}
                onClick={() => handleOnSort(field.sort)}
              >
                <div className="flex gap-2 items-center w-full justify-start">
                  <div className={field?.headerClassName}> {field?.title} </div>
                  {field?.sort && (
                    <div className="flex flex-col">
                      {(field.sort.type === "asc" ||
                        !["asc", "desc"].includes(field.sort.type)) && (
                        <div>
                          <ChevronUpIcon
                            viewBox="0 -2 11 8"
                            width={9}
                            height={9}
                          />
                        </div>
                      )}

                      {(field.sort.type === "desc" ||
                        !["asc", "desc"].includes(field.sort.type)) && (
                        <div>
                          <ChevronDownIcon
                            viewBox={
                              field.sort.type === "desc"
                                ? "0 -2 11 8"
                                : "0 0 11 8"
                            }
                            width={9}
                            height={9}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default TableHeader;
